/*
 * Copyright (C) 2021 Quartet Health, Inc - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 */

import * as React from 'react';
import grid from '@coop/ole/styles/grid';

import styled, { FlattenSimpleInterpolation } from 'styled-components';
import { ViewBox } from 'src/components/LandingPage/medicaid/WaveForms/constants';
import {
  createCalmingWavePath,
  createSimpleWavePath
} from 'src/components/LandingPage/medicaid/WaveForms/waveGenerators';
import { breakpointsPhablet } from 'src/utils/customBreakpoints';

interface Props {
  waveStyles: WaveProps;
}

interface WavePositioning {
  top: string;
  marginBottom: string;
  borderBottom: string;
}

export enum WaveTypes {
  Calm = 'calm',
  Simple = 'simple'
}

interface WaveProps {
  type: WaveTypes;

  baseBreakpoint: WavePositioning;
  smBreakpoint?: WavePositioning;
  phabletBreakpoint?: WavePositioning;
  mdBreakpoint?: WavePositioning;
  lgBreakpoint?: WavePositioning;
  xlBreakpoint?: WavePositioning;

  shouldInvertWave?: boolean;

  waveColor: string;
  zIndex?: FlattenSimpleInterpolation;
  waveBackgroundColor?: string;
  transform?: string;
}

const WaveContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  position: relative;
  width: 100%;
  top: ${(props: WaveProps) => props.baseBreakpoint.top};
  margin-bottom: ${(props: WaveProps) => props.baseBreakpoint.marginBottom};
  border-bottom: ${(props: WaveProps) => props.baseBreakpoint.borderBottom};
  background-color: ${(props: WaveProps) =>
    props.waveBackgroundColor ? props.waveBackgroundColor : 'transparent'};
  ${(props: WaveProps) => (props.zIndex ? props.zIndex : `z-index: 0`)};
  -webkit-transform: ${(props: WaveProps) =>
    props.transform ? props.transform : 'none'};
  transform: ${(props: WaveProps) =>
    props.transform ? props.transform : 'none'};
  @media (min-width: ${grid.breakpoints.sm}) {
    ${(props: WaveProps) =>
      props.smBreakpoint?.top ? `top: ${props.smBreakpoint.top};` : null}
    ${(props: WaveProps) =>
      props.smBreakpoint?.marginBottom
        ? `margin-bottom: ${props.smBreakpoint.marginBottom};`
        : null}
    ${(props: WaveProps) =>
      props.smBreakpoint?.borderBottom
        ? `border-bottom: ${props.smBreakpoint.borderBottom};`
        : null}
  }
  @media (min-width: ${breakpointsPhablet}) {
    ${(props: WaveProps) =>
      props.phabletBreakpoint?.top
        ? `top: ${props.phabletBreakpoint.top};`
        : null}
    ${(props: WaveProps) =>
      props.phabletBreakpoint?.marginBottom
        ? `margin-bottom: ${props.phabletBreakpoint.marginBottom};`
        : null}
    ${(props: WaveProps) =>
      props.phabletBreakpoint?.borderBottom
        ? `border-bottom: ${props.phabletBreakpoint.borderBottom};`
        : null}
  }
  @media (min-width: ${grid.breakpoints.md}) {
    ${(props: WaveProps) =>
      props.mdBreakpoint?.top ? `top: ${props.mdBreakpoint.top};` : null}
    ${(props: WaveProps) =>
      props.mdBreakpoint?.marginBottom
        ? `margin-bottom: ${props.mdBreakpoint.marginBottom};`
        : null}
    ${(props: WaveProps) =>
      props.mdBreakpoint?.borderBottom
        ? `border-bottom: ${props.mdBreakpoint.borderBottom};`
        : null}
  }
  @media (min-width: ${grid.breakpoints.lg}) {
    ${(props: WaveProps) =>
      props.lgBreakpoint?.top ? `top: ${props.lgBreakpoint.top};` : null}
    ${(props: WaveProps) =>
      props.lgBreakpoint?.marginBottom
        ? `margin-bottom: ${props.lgBreakpoint.marginBottom};`
        : null}
    ${(props: WaveProps) =>
      props.lgBreakpoint?.borderBottom
        ? `border-bottom: ${props.lgBreakpoint.borderBottom};`
        : null}
  }
  @media (min-width: ${grid.breakpoints.xl}) {
    ${(props: WaveProps) =>
      props.xlBreakpoint?.top ? `top: ${props.xlBreakpoint.top};` : null}
    ${(props: WaveProps) =>
      props.xlBreakpoint?.marginBottom
        ? `margin-bottom: ${props.xlBreakpoint.marginBottom};`
        : null}
    ${(props: WaveProps) =>
      props.xlBreakpoint?.borderBottom
        ? `border-bottom: ${props.xlBreakpoint.borderBottom};`
        : null}
  }
`;

const SVGContainer = styled.svg`
  flex: 1;
  width: 100%;
  background-color: ${(props: WaveProps) =>
    props.waveBackgroundColor ? props.waveBackgroundColor : 'transparent'};
  transform: none;
`;

const SurfsUpWave = (props: Props) => {
  const shouldInvertWave = props.waveStyles.shouldInvertWave
    ? props.waveStyles.shouldInvertWave
    : false;

  let wavePath;

  if (props.waveStyles.type === WaveTypes.Calm) {
    wavePath = createCalmingWavePath({ shouldInvertWave });
  } else {
    wavePath = createSimpleWavePath({ shouldInvertWave });
  }

  return (
    <WaveContainer {...props} {...props.waveStyles}>
      <SVGContainer
        {...props.waveStyles}
        viewBox={ViewBox}
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d={wavePath}
          fill={props.waveStyles.waveColor}
          stroke="none"
          id={`${props.waveStyles.type}-wave`}
        />
      </SVGContainer>
      <div // This div is here to fix a SVG rendering glitch. The "border-bottom" CSS value and the drawn SVG waveform leave a 2px gap in between each other that this addresses
        style={{
          marginBottom: '-4px',
          top: '-3px',
          position: 'relative',
          height: '4px',
          backgroundColor: props.waveStyles.waveColor
        }}
      />
    </WaveContainer>
  );
};

export default SurfsUpWave;
